import { useState, useEffect } from "react";
import { Link, useLocation } from "react-router-dom";
import { useAuth } from "../../hooks/useAuth";
import { isAuthenticatedUser } from "../../utils/authUser";
import { useTheme } from "../../hooks/useTheme";
import {
    LayoutDashboard,
    ClipboardList,
    Users,
    Calendar,
    ChevronDown,
    Menu,
    UserCheck,
    ShieldCheck,
    Briefcase,
    BriefcaseBusinessIcon,
    Settings,
    UserCircle,
    CreditCard,
    BarChart3,
    Clipboard,
    Moon,
    Sun,
    GraduationCap,
    Building2,
    MessageSquare,
    Gauge,
    MessagesSquare,
    FileSearch,
    BookOpen,
    Rocket,
    LifeBuoy,
    Sparkles,
    ListChecks,
    SlidersHorizontal,
    Megaphone
} from "lucide-react";
import "./Sidebar.css";

function Sidebar({ mobileOpen = false, onMobileClose }) {
    const { user } = useAuth();
    const { isDarkMode, toggleDarkMode } = useTheme();
    const location = useLocation();
    const [featuresOpen, setFeaturesOpen] = useState(true);
    const isActive = (path) => location.pathname === path;

    // Starts fully expanded until the user collapses it themselves.
    const [collapsedPref, setCollapsedPref] = useState(() => {
        const saved = localStorage.getItem('sidebar-collapsed');
        return saved !== null ? JSON.parse(saved) : false;
    });

    // As a mobile slide-in drawer we always show full-width labels, regardless
    // of the persisted desktop collapse preference.
    const collapsed = mobileOpen ? false : collapsedPref;

    const toggleCollapse = () => {
        setCollapsedPref(prev => {
            const newState = !prev;
            localStorage.setItem('sidebar-collapsed', JSON.stringify(newState));
            return newState;
        });
    };

    // Close the mobile drawer after navigating to a new route.
    useEffect(() => {
        if (mobileOpen) onMobileClose?.();
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [location.pathname]);
    // Sub-navigation sections all start open.
    const [accountOpen, setAccountOpen] = useState(true);
    const [myCoachingOpen, setMyCoachingOpen] = useState(true);
    const [resourcesOpen, setResourcesOpen] = useState(true);
    const [adminViewOpen, setAdminViewOpen] = useState(true);
    const [userViewOpen, setUserViewOpen] = useState(true);
    const [employerViewOpen, setEmployerViewOpen] = useState(true);
    const [coachViewOpen, setCoachViewOpen] = useState(true);

    // Auto-open My Coaching dropdown if on one of its sub-pages
    useEffect(() => {
        const myCoachingPaths = ['/coaching', '/resume-analysis', '/mockInterview', '/career-success-index/user'];
        if (myCoachingPaths.some(p => location.pathname.startsWith(p))) {
            setMyCoachingOpen(true);
        }
    }, [location.pathname]);

    // Auto-open Action Plan dropdown if on one of its sub-pages
    useEffect(() => {
        const actionPlanPaths = ['/brandManager', '/actionitems', '/scheduletasks', '/progressreport'];
        if (actionPlanPaths.includes(location.pathname)) {
            setFeaturesOpen(true);
        }
    }, [location.pathname]);

    // Auto-open Resources dropdown if on one of its sub-pages
    useEffect(() => {
        if (location.pathname.startsWith('/resources') || location.pathname === '/contact') {
            setResourcesOpen(true);
        }
    }, [location.pathname]);

    // Auto-open My Account dropdown if on one of its sub-pages
    useEffect(() => {
        const accountPaths = ['/preferences', '/membership', '/employer/apply'];
        if (accountPaths.includes(location.pathname)) {
            setAccountOpen(true);
        }
    }, [location.pathname]);
    // Auto-open Admin View dropdown if on an admin page
    useEffect(() => {
        if (user?.isAdmin) {
            const adminPaths = ['/admin/dashboard', '/admin/users', '/admin/employees', '/admin/lookups', '/admin/memberships', '/admin/jobmanagement', '/admin/surveys', '/admin/interviewmanagement', '/admin/pitchmanagement', '/admin/contact-messages'];
            if (adminPaths.some(path => location.pathname.startsWith(path))) {
                setAdminViewOpen(true);
            }
        }
    }, [location.pathname, user?.isAdmin]);

    // Auto-open User View dropdown for admins if on a user page
    useEffect(() => {
        if (user?.isAdmin) {
            // Coach-side paths must NOT trigger the user view dropdown.
            const coachOnlyPaths = ['/coaching/dashboard', '/coaching/students', '/coaching/prospects', '/coaching/reporting', '/coaching/preferences', '/coaching/membership', '/coaching/profile'];
            if (coachOnlyPaths.some(path => location.pathname.startsWith(path))) {
                return;
            }
            // User-side paths (exact match for /coaching roots to avoid clashing with coach subpaths).
            const userExactCoachingPaths = ['/coaching', '/coaching/find', '/coaching/suggestions', '/coaching/feedback', '/coaching/invite'];
            const userPrefixPaths = ['/userdashboard', '/actionitems', '/scheduletasks', '/progressreport', '/preferences', '/membership', '/jobbrowser', '/applicationsTracker', '/brandManager', '/resume-analysis'];
            const isUserPage =
                userExactCoachingPaths.includes(location.pathname) ||
                userPrefixPaths.some(path => location.pathname.startsWith(path));
            if (isUserPage) {
                setUserViewOpen(true);
            }
        }
    }, [location.pathname, user?.isAdmin]);

    // Auto-open Employer View dropdown for admins if on an employer page
    useEffect(() => {
        if (user?.isAdmin) {
            const employerViewPaths = ['/employer/dashboard'];
            if (employerViewPaths.some(path => location.pathname.startsWith(path))) {
                setEmployerViewOpen(true);
            }
        }
    }, [location.pathname, user?.isAdmin]);

    // Auto-open Coach View dropdown for admins if on a coach page
    useEffect(() => {
        if (user?.isAdmin) {
            const coachViewPaths = ['/coaching/dashboard', '/coaching/students', '/coaching/prospects', '/coaching/reporting', '/coaching/preferences', '/coaching/membership', '/coaching/profile'];
            if (coachViewPaths.some(path => location.pathname.startsWith(path))) {
                setCoachViewOpen(true);
            }
        }
    }, [location.pathname, user?.isAdmin]);

    // Only render for a real session (defense in depth: reject ghost / empty objects)
    if (!isAuthenticatedUser(user)) return null;

    return (
        <>
        {mobileOpen && (
            <div className="sidebar-backdrop md:hidden" onClick={onMobileClose} />
        )}
        <div className={`sidebar-slot ${collapsed ? "collapsed" : ""}`}>
        <aside className={`sidebar-container ${collapsed ? "collapsed" : ""} ${mobileOpen ? "mobile-open" : ""}`}>
            <div className="sidebar-header">
                <button onClick={toggleCollapse} className="collapse-btn">
                    <Menu className="h-5 w-5" />
                </button>
                {!collapsed && (
                    <div className="sidebar-title">
                        <h2>My BizBrand</h2>
                        {/* <p>Professional Portal</p> */}
                    </div>
                )}
            </div>

            <ul className="sidebar-menu">
                {/* Admin-Only Section - Show first for admins */}
                {user?.isAdmin && (
                    <>
                        {/* Admin View Dropdown */}
                        <li className={`dropdown-section ${adminViewOpen ? "open" : ""}`}>
                            <button
                                onClick={() => {
                                    if (collapsed) setCollapsedPref(false);
                                    setAdminViewOpen(!adminViewOpen);
                                }}
                                className="sidebar-link dropdown-toggle"
                            >
                                <ShieldCheck size={22} />
                                {!collapsed && <span>Admin View</span>}
                                {!collapsed && (
                                    <ChevronDown
                                        className={`arrow ${adminViewOpen ? "rotate" : ""}`}
                                        size={14}
                                    />
                                )}
                            </button>

                            {!collapsed && adminViewOpen && (
                                <ul className="dropdown-menu">
                                    <li>
                                        <Link to="/admin/dashboard" className={`sidebar-sublink ${isActive('/admin/dashboard') ? 'active' : ''}`}>
                                            <LayoutDashboard size={18} />
                                            <span>Admin Dashboard</span>
                                        </Link>
                                    </li>

                                    <li className="sidebar-section-label">Users / Members</li>
                                    <li>
                                        <Link to="/admin/users" className={`sidebar-sublink ${isActive('/admin/users') ? 'active' : ''}`}>
                                            <Users size={18} />
                                            <span>User Management</span>
                                        </Link>
                                    </li>
                                    <li>
                                        <Link to="/admin/employees" className={`sidebar-sublink ${isActive('/admin/employees') ? 'active' : ''}`}>
                                            <UserCheck size={18} />
                                            <span>Employee Management</span>
                                        </Link>
                                    </li>
                                    <li>
                                        <Link to="/admin/memberships" className={`sidebar-sublink ${isActive('/admin/memberships') ? 'active' : ''}`}>
                                            <CreditCard size={18} />
                                            <span>Membership Management</span>
                                        </Link>
                                    </li>

                                    <li className="sidebar-section-label">AI Feature</li>
                                    <li>
                                        <Link to="/admin/surveys" className={`sidebar-sublink ${isActive('/admin/surveys') ? 'active' : ''}`}>
                                            <ClipboardList size={18} />
                                            <span>Survey Management</span>
                                        </Link>
                                    </li>
                                    <li>
                                        <Link to="/admin/interviewmanagement" className={`sidebar-sublink ${isActive('/admin/interviewmanagement') ? 'active' : ''}`}>
                                            <MessagesSquare size={18} />
                                            <span>Interview Management</span>
                                        </Link>
                                    </li>
                                    <li>
                                        <Link to="/admin/pitchmanagement" className={`sidebar-sublink ${isActive('/admin/pitchmanagement') ? 'active' : ''}`}>
                                            <Megaphone size={18} />
                                            <span>Pitch Management</span>
                                        </Link>
                                    </li>
                                    <li>
                                        <Link to="/admin/resume-scoring" className={`sidebar-sublink ${isActive('/admin/resume-scoring') ? 'active' : ''}`}>
                                            <SlidersHorizontal size={18} />
                                            <span>Resume Analysis Management</span>
                                        </Link>
                                    </li>

                                    <li className="sidebar-section-label">General</li>
                                    <li>
                                        <Link to="/admin/lookups" className={`sidebar-sublink ${isActive('/admin/lookups') ? 'active' : ''}`}>
                                            <Settings size={18} />
                                            <span>Lookup Management</span>
                                        </Link>
                                    </li>
                                    <li>
                                        <Link to="/admin/jobmanagement" className={`sidebar-sublink ${isActive('/admin/jobmanagement') ? 'active' : ''}`}>
                                            <BriefcaseBusinessIcon size={18} />
                                            <span>Job Management</span>
                                        </Link>
                                    </li>
                                    <li>
                                        <Link to="/admin/contact-messages" className={`sidebar-sublink ${isActive('/admin/contact-messages') ? 'active' : ''}`}>
                                            <MessageSquare size={18} />
                                            <span>Contact Messages</span>
                                        </Link>
                                    </li>
                                </ul>
                            )}
                        </li>

                        {/* User View Dropdown for Admins */}
                        <li className={`dropdown-section ${userViewOpen ? "open" : ""}`}>
                            <button 
                                onClick={() => {
                                    if (collapsed) setCollapsedPref(false);
                                    setUserViewOpen(!userViewOpen);
                                }}
                                className="sidebar-link dropdown-toggle"
                            >
                                <UserCircle size={22} />
                                {!collapsed && <span>User View</span>}
                                {!collapsed && (
                                    <ChevronDown
                                        className={`arrow ${userViewOpen ? "rotate" : ""}`}
                                        size={14}
                                    />
                                )}
                            </button>

                            {!collapsed && userViewOpen && (
                                <ul className="dropdown-menu">
                                    <li>
                                        <Link to="/userdashboard" className={`sidebar-sublink ${isActive('/userdashboard') ? 'active' : ''}`}>
                                            <LayoutDashboard size={18} />
                                            <span>Dashboard</span>
                                        </Link>
                                    </li>
                                    <li>
                                        <Link to="/jobbrowser" className={`sidebar-sublink ${isActive('/jobbrowser') ? 'active' : ''}`}>
                                            <Briefcase size={18} />
                                            <span>Job Browser</span>
                                        </Link>
                                    </li>
                                    <li>
                                        <Link to="/applicationsTracker" className={`sidebar-sublink ${isActive('/applicationsTracker') ? 'active' : ''}`}>
                                            <Clipboard size={18} />
                                            <span>Job Tracker</span>
                                        </Link>
                                    </li>

                                    <li className="sidebar-section-label">My Coaching</li>
                                    <li>
                                        <Link to="/career-success-index/user" className={`sidebar-sublink ${isActive('/career-success-index/user') ? 'active' : ''}`}>
                                            <Gauge size={18} />
                                            <span>Career Success Index</span>
                                        </Link>
                                    </li>
                                    <li>
                                        <Link to="/mockInterview" className={`sidebar-sublink ${isActive('/mockInterview') ? 'active' : ''}`}>
                                            <MessagesSquare size={18} />
                                            <span>Mock Interview</span>
                                        </Link>
                                    </li>
                                    <li>
                                        <Link to="/resume-analysis" className={`sidebar-sublink ${isActive('/resume-analysis') ? 'active' : ''}`}>
                                            <FileSearch size={18} />
                                            <span>Resume Analysis</span>
                                        </Link>
                                    </li>
                                    <li>
                                        <Link to="/coaching" className={`sidebar-sublink ${location.pathname.startsWith('/coaching') && !location.pathname.startsWith('/coaching/dashboard') ? 'active' : ''}`}>
                                            <Users size={18} />
                                            <span>Partner Coaching</span>
                                        </Link>
                                    </li>

                                    <li className="sidebar-section-label">Action Plan</li>
                                    <li>
                                        <Link to="/progressreport" className={`sidebar-sublink ${isActive('/progressreport') ? 'active' : ''}`}>
                                            <BarChart3 size={18} />
                                            <span>Progress Report</span>
                                        </Link>
                                    </li>
                                    <li>
                                        <Link to="/brandManager" className={`sidebar-sublink ${isActive('/brandManager') ? 'active' : ''}`}>
                                            <Sparkles size={18} />
                                            <span>Brand Manager</span>
                                        </Link>
                                    </li>
                                    <li>
                                        <Link to="/actionitems" className={`sidebar-sublink ${isActive('/actionitems') ? 'active' : ''}`}>
                                            <ListChecks size={18} />
                                            <span>Action Items</span>
                                        </Link>
                                    </li>
                                    <li>
                                        <Link to="/scheduletasks" className={`sidebar-sublink ${isActive('/scheduletasks') ? 'active' : ''}`}>
                                            <Calendar size={18} />
                                            <span>Schedule</span>
                                        </Link>
                                    </li>

                                    <li className="sidebar-section-label">Resources</li>
                                    <li>
                                        <Link to="/resources/getting-started" className={`sidebar-sublink ${isActive('/resources/getting-started') ? 'active' : ''}`}>
                                            <Rocket size={18} />
                                            <span>Getting Started</span>
                                        </Link>
                                    </li>
                                    <li>
                                        <Link to="/resources/tutorials-support" className={`sidebar-sublink ${isActive('/resources/tutorials-support') ? 'active' : ''}`}>
                                            <LifeBuoy size={18} />
                                            <span>Tutorials</span>
                                        </Link>
                                    </li>
                                    <li>
                                        <Link to="/contact" className={`sidebar-sublink ${isActive('/contact') ? 'active' : ''}`}>
                                            <MessageSquare size={18} />
                                            <span>Contact Support</span>
                                        </Link>
                                    </li>

                                    <li className="sidebar-section-label">My Account</li>
                                    <li>
                                        <Link to="/preferences" className={`sidebar-sublink ${isActive('/preferences') ? 'active' : ''}`}>
                                            <UserCircle size={18} />
                                            <span>My Preferences</span>
                                        </Link>
                                    </li>
                                    <li>
                                        <Link to="/membership" className={`sidebar-sublink ${isActive('/membership') ? 'active' : ''}`}>
                                            <CreditCard size={18} />
                                            <span>My Membership</span>
                                        </Link>
                                    </li>
                                    <li>
                                        <Link to="/employer/apply" className={`sidebar-sublink ${isActive('/employer/apply') ? 'active' : ''}`}>
                                            <Building2 size={18} />
                                            <span>Apply as Employer</span>
                                        </Link>
                                    </li>
                                </ul>
                            )}
                        </li>

                        {/* Employer View Dropdown for Admins */}
                        <li className={`dropdown-section ${employerViewOpen ? "open" : ""}`}>
                            <button
                                onClick={() => {
                                    if (collapsed) setCollapsedPref(false);
                                    setEmployerViewOpen(!employerViewOpen);
                                }}
                                className="sidebar-link dropdown-toggle"
                            >
                                <Building2 size={22} />
                                {!collapsed && <span>Employer View</span>}
                                {!collapsed && (
                                    <ChevronDown
                                        className={`arrow ${employerViewOpen ? "rotate" : ""}`}
                                        size={14}
                                    />
                                )}
                            </button>

                            {!collapsed && employerViewOpen && (
                                <ul className="dropdown-menu">
                                    <li>
                                        <Link to="/employer/dashboard" className={`sidebar-sublink ${isActive('/employer/dashboard') ? 'active' : ''}`}>
                                            <LayoutDashboard size={18} />
                                            <span>Employer Dashboard</span>
                                        </Link>
                                    </li>
                                </ul>
                            )}
                        </li>

                        {/* Coach View Dropdown for Admins */}
                        <li className={`dropdown-section ${coachViewOpen ? "open" : ""}`}>
                            <button
                                onClick={() => {
                                    if (collapsed) setCollapsedPref(false);
                                    setCoachViewOpen(!coachViewOpen);
                                }}
                                className="sidebar-link dropdown-toggle"
                            >
                                <GraduationCap size={22} />
                                {!collapsed && <span>Coach View</span>}
                                {!collapsed && (
                                    <ChevronDown
                                        className={`arrow ${coachViewOpen ? "rotate" : ""}`}
                                        size={14}
                                    />
                                )}
                            </button>

                            {!collapsed && coachViewOpen && (
                                <ul className="dropdown-menu">
                                    <li>
                                        <Link to="/coaching/dashboard" className={`sidebar-sublink ${isActive('/coaching/dashboard') ? 'active' : ''}`}>
                                            <LayoutDashboard size={18} />
                                            <span>Coaching Dashboard</span>
                                        </Link>
                                    </li>
                                    <li>
                                        <Link to="/coaching/students" className={`sidebar-sublink ${isActive('/coaching/students') ? 'active' : ''}`}>
                                            <Users size={18} />
                                            <span>My Students</span>
                                        </Link>
                                    </li>
                                    <li>
                                        <Link to="/coaching/prospects" className={`sidebar-sublink ${isActive('/coaching/prospects') ? 'active' : ''}`}>
                                            <UserCheck size={18} />
                                            <span>My Prospects</span>
                                        </Link>
                                    </li>
                                    <li>
                                        <Link to="/coaching/reporting" className={`sidebar-sublink ${isActive('/coaching/reporting') ? 'active' : ''}`}>
                                            <BarChart3 size={18} />
                                            <span>Coach Reporting</span>
                                        </Link>
                                    </li>
                                    <li>
                                        <Link to="/coaching/preferences" className={`sidebar-sublink ${isActive('/coaching/preferences') ? 'active' : ''}`}>
                                            <Settings size={18} />
                                            <span>My Program</span>
                                        </Link>
                                    </li>
                                    <li>
                                        <Link to="/coaching/membership" className={`sidebar-sublink ${isActive('/coaching/membership') ? 'active' : ''}`}>
                                            <CreditCard size={18} />
                                            <span>My Membership</span>
                                        </Link>
                                    </li>
                                    <li>
                                        <Link to="/coaching/profile" className={`sidebar-sublink ${isActive('/coaching/profile') ? 'active' : ''}`}>
                                            <UserCircle size={18} />
                                            <span>My Profile</span>
                                        </Link>
                                    </li>
                                </ul>
                            )}
                        </li>
                    </>
                )}

                {/* Coach-Only Section */}
                {user?.isCoach && (
                    <>
                        <li>
                            <Link to="/coaching/dashboard" className={`sidebar-link ${isActive('/coaching/dashboard') ? 'active' : ''}`}>
                                <LayoutDashboard size={22} />
                                {!collapsed && <span>Coaching Dashboard</span>}
                            </Link>
                        </li>
                        <li>
                            <Link to="/coaching/students" className={`sidebar-link ${isActive('/coaching/students') ? 'active' : ''}`}>
                                <Users size={22} />
                                {!collapsed && <span>My Students</span>}
                            </Link>
                        </li>
                        <li>
                            <Link to="/coaching/prospects" className={`sidebar-link ${isActive('/coaching/prospects') ? 'active' : ''}`}>
                                <UserCheck size={22} />
                                {!collapsed && <span>My Prospects</span>}
                            </Link>
                        </li>
                        <li>
                            <Link to="/coaching/reporting" className={`sidebar-link ${isActive('/coaching/reporting') ? 'active' : ''}`}>
                                <BarChart3 size={22} />
                                {!collapsed && <span>Coach Reporting</span>}
                            </Link>
                        </li>
                        <li>
                            <Link to="/coaching/preferences" className={`sidebar-link ${isActive('/coaching/preferences') ? 'active' : ''}`}>
                                <Settings size={22} />
                                {!collapsed && <span>My Program</span>}
                            </Link>
                        </li>
                        <li>
                            <Link to="/coaching/membership" className={`sidebar-link ${isActive('/coaching/membership') ? 'active' : ''}`}>
                                <CreditCard size={22} />
                                {!collapsed && <span>My Membership</span>}
                            </Link>
                        </li>
                        <li>
                            <Link to="/coaching/profile" className={`sidebar-link ${isActive('/coaching/profile') ? 'active' : ''}`}>
                                <UserCircle size={22} />
                                {!collapsed && <span>My Profile</span>}
                            </Link>
                        </li>
                    </>
                )}

                {/* Regular User Menu (show for clients and employers, but not admin/coach) */}
                {!user?.isAdmin && !user?.isCoach && (
                    <>
                        <li>
                            <Link to="/userdashboard" className={`sidebar-link ${isActive('/userdashboard') ? 'active' : ''}`}>
                                <LayoutDashboard size={22} />
                                {!collapsed && <span>Dashboard</span>}
                            </Link>
                        </li>

                        <li>
                               <Link to="/jobbrowser" className={`sidebar-link ${isActive('/jobbrowser') || isActive('/jobListings') ? 'active' : ''}`}>
                                <Briefcase size={22} />
                                {!collapsed && <span>Job Browser</span>}
                            </Link>
                        </li>

                        <li>
                            <Link to="/applicationsTracker" className={`sidebar-link ${isActive('/applicationsTracker') ? 'active' : ''}`}>
                                <Clipboard size={22} />
                                {!collapsed && <span>Job Tracker</span>}
                            </Link>
                        </li>

                        {/* My Coaching Dropdown Section (AI coaching tools) */}
                        <li className={`dropdown-section ${myCoachingOpen ? "open" : ""}`}>
                            <button
                                onClick={() => {
                                    if (collapsed) setCollapsedPref(false);
                                    setMyCoachingOpen(!myCoachingOpen);
                                }}
                                className="sidebar-link dropdown-toggle"
                            >
                                <GraduationCap size={22} />
                                {!collapsed && <span>My Coaching</span>}
                                {!collapsed && <ChevronDown className={`arrow ${myCoachingOpen ? "rotate" : ""}`} size={14} />}
                            </button>

                            {!collapsed && myCoachingOpen && (
                                <ul className="dropdown-menu">
                                    <li>
                                        <Link to="/career-success-index/user" className={`sidebar-sublink ${isActive('/career-success-index/user') ? 'active' : ''}`}>
                                            <Gauge size={18} />
                                            <span>Career Success Index</span>
                                        </Link>
                                    </li>
                                    <li>
                                        <Link to="/mockInterview" className={`sidebar-sublink ${isActive('/mockInterview') ? 'active' : ''}`}>
                                            <MessagesSquare size={18} />
                                            <span>Mock Interview</span>
                                        </Link>
                                    </li>
                                    <li>
                                        <Link to="/resume-analysis" className={`sidebar-sublink ${isActive('/resume-analysis') ? 'active' : ''}`}>
                                            <FileSearch size={18} />
                                            <span>Resume Analysis</span>
                                        </Link>
                                    </li>
                                    <li>
                                        <Link to="/coaching" className={`sidebar-sublink ${location.pathname.startsWith('/coaching') && !location.pathname.startsWith('/coaching/dashboard') ? 'active' : ''}`}>
                                            <Users size={18} />
                                            <span>Partner Coaching</span>
                                        </Link>
                                    </li>
                                </ul>
                            )}
                        </li>

                        {/* Action Plan Dropdown Section */}
                        <li className={`dropdown-section ${featuresOpen ? "open" : ""}`}>
                            <button onClick={() => setFeaturesOpen(!featuresOpen)} className="sidebar-link dropdown-toggle">
                                <ClipboardList size={22} />
                                {!collapsed && <span>Action Plan</span>}
                                {!collapsed && <ChevronDown className={`arrow ${featuresOpen ? "rotate" : ""}`} size={14} />}
                            </button>

                            {!collapsed && featuresOpen && (
                                <ul className="dropdown-menu">
                                    <li>
                                        <Link to="/progressreport" className={`sidebar-sublink ${isActive('/progressreport') ? 'active' : ''}`}>
                                            <BarChart3 size={18} />
                                            <span>Progress Report</span>
                                        </Link>
                                    </li>
                                    <li>
                                        <Link to="/brandManager" className={`sidebar-sublink ${isActive('/brandManager') ? 'active' : ''}`}>
                                            <Sparkles size={18} />
                                            <span>Brand Manager</span>
                                        </Link>
                                    </li>
                                    <li>
                                        <Link to="/actionitems" className={`sidebar-sublink ${isActive('/actionitems') ? 'active' : ''}`}>
                                            <ListChecks size={18} />
                                            <span>Action Items</span>
                                        </Link>
                                    </li>
                                    <li>
                                        <Link to="/scheduletasks" className={`sidebar-sublink ${isActive('/scheduletasks') ? 'active' : ''}`}>
                                            <Calendar size={18} />
                                            <span>Schedule</span>
                                        </Link>
                                    </li>
                                </ul>
                            )}
                        </li>

                        {/* Resources Dropdown Section */}
                        <li className={`dropdown-section ${resourcesOpen ? "open" : ""}`}>
                            <button
                                onClick={() => {
                                    if (collapsed) setCollapsedPref(false);
                                    setResourcesOpen(!resourcesOpen);
                                }}
                                className="sidebar-link dropdown-toggle"
                            >
                                <BookOpen size={22} />
                                {!collapsed && <span>Resources</span>}
                                {!collapsed && <ChevronDown className={`arrow ${resourcesOpen ? "rotate" : ""}`} size={14} />}
                            </button>

                            {!collapsed && resourcesOpen && (
                                <ul className="dropdown-menu">
                                    <li>
                                        <Link to="/resources/getting-started" className={`sidebar-sublink ${isActive('/resources/getting-started') ? 'active' : ''}`}>
                                            <Rocket size={18} />
                                            <span>Getting Started</span>
                                        </Link>
                                    </li>
                                    <li>
                                        <Link to="/resources/tutorials-support" className={`sidebar-sublink ${isActive('/resources/tutorials-support') ? 'active' : ''}`}>
                                            <LifeBuoy size={18} />
                                            <span>Tutorials</span>
                                        </Link>
                                    </li>
                                    <li>
                                        <Link to="/contact" className={`sidebar-sublink ${isActive('/contact') ? 'active' : ''}`}>
                                            <MessageSquare size={18} />
                                            <span>Contact Support</span>
                                        </Link>
                                    </li>
                                </ul>
                            )}
                        </li>

                        {user?.isEmployer && (
                        <li>
                            <Link to="/employer/dashboard" className={`sidebar-link ${isActive('/employer/dashboard') ? 'active' : ''}`}>
                                <Building2 size={22} />
                                {!collapsed && <span>Employer Dashboard</span>}
                            </Link>
                        </li>
                        )}

                        {/* My Account Dropdown Section */}
                        <li className={`dropdown-section ${accountOpen ? "open" : ""}`}>
                            <button
                                onClick={() => {
                                    if (collapsed) setCollapsedPref(false);
                                    setAccountOpen(!accountOpen);
                                }}
                                className={`sidebar-link dropdown-toggle ${
                                    isActive('/preferences') || isActive('/membership') ? 'active' : ''
                                }`}
                            >
                                <Settings size={22} />
                                {!collapsed && <span>My Account</span>}
                                {!collapsed && (
                                    <ChevronDown
                                        className={`arrow ${accountOpen ? "rotate" : ""}`}
                                        size={14}
                                    />
                                )}
                            </button>

                            {!collapsed && accountOpen && (
                                <ul className="dropdown-menu">
                                    <li>
                                        <Link
                                            to="/preferences"
                                            className={`sidebar-sublink ${isActive('/preferences') ? 'active' : ''}`}
                                        >
                                            <UserCircle size={18} />
                                            <span>My Preferences</span>
                                        </Link>
                                    </li>
                                    <li>
                                        <Link
                                            to="/membership"
                                            className={`sidebar-sublink ${isActive('/membership') ? 'active' : ''}`}
                                        >
                                            <CreditCard size={18} />
                                            <span>My Membership</span>
                                        </Link>
                                    </li>
                                    {!user?.isEmployer && (
                                    <li>
                                        <Link
                                            to="/employer/apply"
                                            className={`sidebar-sublink ${isActive('/employer/apply') ? 'active' : ''}`}
                                        >
                                            <Building2 size={18} />
                                            <span>Apply as Employer</span>
                                        </Link>
                                    </li>
                                    )}
                                </ul>
                            )}
                        </li>
                    </>
                )}
            </ul>

            <div className="sidebar-footer">
                <button
                    onClick={toggleDarkMode}
                    className="sidebar-link"
                    title={isDarkMode ? 'Switch to Light Mode' : 'Switch to Dark Mode'}
                    style={{ marginTop: '8px' }}
                >
                    {isDarkMode ? <Sun size={22} /> : <Moon size={22} />}
                    {!collapsed && <span>{isDarkMode ? 'Light Mode' : 'Dark Mode'}</span>}
                </button>
            </div>
        </aside>
        </div>
        </>
    );
}

export default Sidebar;